import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Eye,
  Filter,
  MoreHorizontal,
  RefreshCcw,
  Search,
  XCircle,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useDemoStore } from '../../store'
import type { ApprovalStatus } from '../../types'
import { DemoNotice, PageTitle, StatusBadge } from './shared'

export function ApprovalsPage() {
  const { approvals, updateApproval, apiMode } = useDemoStore()
  const [selectedId, setSelectedId] = useState<string | undefined>(approvals[0]?.id)
  const [comment, setComment] = useState('')
  const [deciding, setDeciding] = useState(false)
  const [decisionError, setDecisionError] = useState<string | null>(null)
  const selected = approvals.find(item => item.id === selectedId)

  useEffect(() => {
    if (!approvals.length) {
      setSelectedId(undefined)
      return
    }
    if (!approvals.some(item => item.id === selectedId)) setSelectedId(approvals[0].id)
  }, [approvals, selectedId])

  const decide = async (status: ApprovalStatus) => {
    if (!selected) return
    if (selected.isLive && apiMode !== 'live') {
      setDecisionError('The local Worker must be connected to record this approval decision.')
      return
    }

    setDeciding(true)
    setDecisionError(null)
    try {
      await updateApproval(selected.id, status, comment.trim())
      setComment('')
    } catch (error) {
      setDecisionError(error instanceof Error ? error.message : 'The approval decision could not be recorded.')
    } finally {
      setDeciding(false)
    }
  }

  return <>
    <PageTitle title="Approvals" copy="One accountable inbox for public output, outbound action, spend, deployment and sensitive system changes." />
    <DemoNotice />
    <div className="approval-layout">
      <section className="approval-list">
        <div className="approval-list__toolbar"><div className="search-field"><Search size={15} /><input placeholder="Search approvals…" /></div><button className="icon-button"><Filter size={16} /></button></div>
        {approvals.length ? approvals.map(item => <button className={item.id === selectedId ? 'active' : ''} key={item.id} onClick={() => { setSelectedId(item.id); setDecisionError(null); setComment('') }}><span className={`risk-dot risk-dot--${item.risk.toLowerCase()}`} /><div><small>{item.isLive ? 'LIVE WORKFLOW APPROVAL' : item.type}</small><strong>{item.title}</strong><span>{item.requestedBy}</span></div><StatusBadge tone={item.status === 'approved' ? 'good' : item.status === 'rejected' ? 'danger' : item.status === 'returned' ? 'warn' : 'neutral'}>{item.status}</StatusBadge></button>) : <div className="app-panel"><p>No approval items are waiting.</p></div>}
      </section>
      {selected && <section className="approval-detail">
        <div className="approval-detail__head"><div><small>{selected.isLive ? 'LIVE WORKFLOW APPROVAL' : selected.type}</small><h2>{selected.title}</h2><p>Requested by {selected.requestedBy} · {selected.due}</p></div><button className="icon-button"><MoreHorizontal /></button></div>
        <div className="approval-facts"><div><span>Risk</span><StatusBadge tone={selected.risk === 'Low' ? 'good' : selected.risk === 'High' ? 'danger' : 'warn'}>{selected.risk}</StatusBadge></div><div><span>External cost</span><strong>{selected.cost}</strong></div><div><span>Current state</span><strong>{selected.status}</strong></div></div>
        <article className="proposal-preview"><div className="proposal-preview__bar"><Eye size={16} />Proposed output preview</div><h3>Prepared action</h3><p>{selected.summary}</p><blockquote>{selected.isLive ? `This decision will be written to Tenant 001 D1${selected.workflowRunId ? ` against run ${selected.workflowRunId}` : ''}. It will not execute an external action.` : 'This is a pilot readiness gate. No consequential action has occurred.'}</blockquote></article>
        <div className="policy-checks"><h3>Policy checks</h3><ul><li><CheckCircle2 />Tenant 001 scope enforced</li><li><CheckCircle2 />No external action performed</li><li><CheckCircle2 />Audit decision required</li><li><AlertTriangle />Approval does not unlock send, publish, spend or deletion</li></ul></div>
        <label className="comment-field">Reviewer comment<textarea rows={3} value={comment} onChange={event => setComment(event.target.value)} placeholder="Add context for the audit history…" /></label>
        {decisionError && <p>{decisionError}</p>}
        <div className="approval-actions"><button className="button button--danger" onClick={() => void decide('rejected')} disabled={deciding || selected.status !== 'pending'}><XCircle size={16} /> Reject</button><button className="button button--outline" onClick={() => void decide('returned')} disabled={deciding || selected.status !== 'pending'}><RefreshCcw size={16} /> Return for revision</button><button className="button button--gold" onClick={() => void decide('approved')} disabled={deciding || selected.status !== 'pending'}><Check size={16} /> {deciding ? 'Recording…' : 'Approve'}</button></div>
      </section>}
    </div>
  </>
}
